// ADR 5.9.26p §2 — the register door.
//
// One field wider than sign-in, and that field is not decoration:
// `accepted_terms` is `#[serde(default)]` on the server and rejected when
// `false` (`routes/auth/mod.rs:44`), so an unticked box is a 400 rather than a
// quieter account. The checkbox is therefore `required` here and the submit
// button is what enforces it in the browser (AC8).

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { Anchor } from '../ui/Anchor'
import { Button, FOCUS_RING } from '../ui/Button'
import { CheckboxField } from '../ui/Field'
import { SectionHeading } from '../ui/SectionHeading'
import { CredentialStatus, EmailPasswordFields, useCredentialSubmit } from './CredentialFields'
import { OfflineHint } from '../offline/OfflineHint'
import { useOnlineStatus } from '../offline/onlineSignal'
import { PageFrame } from '../layout/frame/PageFrame'
import { withNext } from '../app/routeHelpers'

/**
 * The terms line, assembled from the three keys the phones already carry.
 *
 * ADR 8.9.26g §3 — `ui/CheckboxField` rather than a raw `<input type=
 * "checkbox">`: the label is rich (it carries the terms link), which is why the
 * primitive takes a `ReactNode` label rather than a string.
 */
function TermsCheckbox({
  accepted,
  onChange,
  disabled,
}: {
  accepted: boolean
  onChange: (accepted: boolean) => void
  disabled: boolean
}) {
  const { t } = useTranslation()
  return (
    <CheckboxField
      required
      disabled={disabled}
      checked={accepted}
      onChange={onChange}
      label={
        <>
          {t('auth_terms_prefix')}
          <a
            // ADR 15.9.26aj §5 — the ring directly: an external `target="_blank"`
            // link, which `Anchor`'s two branches do not spell.
            className={`rounded underline ${FOCUS_RING}`}
            href="https://getlingara.com/terms"
            target="_blank"
            rel="noreferrer"
          >
            {t('auth_terms_link')}
          </a>
        </>
      }
    />
  )
}

export function RegisterPanel() {
  const { t } = useTranslation()
  const form = useCredentialSubmit('register')
  const [accepted, setAccepted] = useState(false)
  // ADR 23.9.26v §6 — registering needs the network.
  const online = useOnlineStatus()
  const [params] = useSearchParams()

  return (
    <PageFrame width="form" as="section" className="space-y-4">
      <SectionHeading>{t('auth_create_account')}</SectionHeading>
      <form className="space-y-3" onSubmit={form.submit}>
        <EmailPasswordFields form={form} passwordAutoComplete="new-password" />
        <TermsCheckbox accepted={accepted} onChange={setAccepted} disabled={form.busy} />
        <Button type="submit" variant="primary" className="w-full" disabled={form.busy || !online}>
          {t('auth_register')}
        </Button>
        <OfflineHint />
        <CredentialStatus form={form} />
      </form>
      {/* Backend ADR 25.9.26n §5 — keeps `next`, so the way back to sign-in still returns to it. */}
      <Anchor className="block" to={withNext('/signin', params.get('next'))}>
        {t('auth_already_have_account')}
      </Anchor>
    </PageFrame>
  )
}
